"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Upload = void 0;
const fs_1 = __importDefault(require("fs"));
const url_search_params_1 = __importDefault(require("@ungap/url-search-params"));
const APIErrors_1 = require("./resources/APIErrors");
const APIHTTPErrorCodes_1 = require("./resources/APIHTTPErrorCodes");
const APITasks_1 = require("./resources/APITasks");
const APIAbortController_1 = require("./APIAbortController");
const FileOps_1 = require("./FileOps");
const FSIServerClientInterface_1 = require("./FSIServerClientInterface");
const FSIServerClientUtils_1 = require("./FSIServerClientUtils");
const TaskProgress_1 = require("./TaskProgress");
const index_1 = require("./index");
const LogLevel_1 = require("./LogLevel");
const modeNode = FSIServerClientUtils_1.FSIServerClientUtils.GET_MODE_NODE();
class Upload {
    constructor(classInit, taskController) {
        this.classInit = classInit;
        this.taskController = taskController;
        this.client = classInit.client;
        this.com = classInit.com;
        this.baseURL = this.client.getServerBaseQuery();
    }
    store(source, targetPath, options = {}) {
        if (APIAbortController_1.APIAbortController.IS_ABORTED(options.abortController)) {
            return this.com.getAbortPromise();
        }
        const self = this;
        let sourcePath;
        let entry;
        let file;
        if (typeof (source) === "string") {
            sourcePath = source;
            entry = null;
            file = null;
        }
        else {
            sourcePath = source.path;
            entry = source;
            file = entry.file;
        }
        const rejectWithError = (reject, err) => {
            if (this.com.isAbortError(err)) {
                return reject(self.com.err.get(APIErrors_1.APIErrors.upload, [sourcePath], APIErrors_1.APIErrors.userAborted));
            }
            else {
                const mainError = self.com.err.get(APIErrors_1.APIErrors.upload, [sourcePath]);
                mainError.setSubError(err);
                return reject(mainError);
            }
        };
        if (options.fnProgress !== undefined || options._fnQueueProgress !== undefined) {
            options._taskProgress = new TaskProgress_1.TaskProgress();
            options._taskProgress.currentTask = this.com.taskSupplier.get(APITasks_1.APITasks.uploadFile, [sourcePath]);
        }
        if (entry && entry.type === "directory" && options.flattenTargetPath) {
            this.taskController.setCurrentTask(LogLevel_1.LogLevel.trace, APITasks_1.APITasks.skipUploadDirFlatten, [sourcePath]);
            return FSIServerClientInterface_1.FSIServerClientInterface.GET_TRUE_PROMISE();
        }
        targetPath = FSIServerClientUtils_1.FSIServerClientUtils.NORMALIZE_PATH(targetPath);
        const pd = FSIServerClientUtils_1.FSIServerClientUtils.FILE_AND_PATH(sourcePath);
        let mimeType = this.com.lookupMimeType(pd.dir);
        if (!mimeType)
            mimeType = "application/unknown";
        const fileName = (options.fileName) ? options.fileName : pd.dir;
        let relPath = "";
        if (!options.flattenTargetPath) {
            relPath = FSIServerClientUtils_1.FSIServerClientUtils.MAKE_RELATIVE_PATH(pd.path);
        }
        const targetDir = FSIServerClientUtils_1.FSIServerClientUtils.NORMALIZE_PATH(targetPath + relPath);
        const finalPath = targetDir + fileName;
        const onCreateDirError = async (httpStatus) => {
            if (httpStatus === 409) {
                self.callProgress(LogLevel_1.LogLevel.trace, options, APITasks_1.APITasks.skipUploadDir, [finalPath]);
                return {
                    continue: true,
                    reply: ""
                };
            }
            else {
                return {
                    continue: false,
                    reply: ""
                };
            }
        };
        if (entry && entry.type === "directory") {
            this.taskController.setCurrentTask(LogLevel_1.LogLevel.debug, APITasks_1.APITasks.createDir, [finalPath]);
            return new FileOps_1.FileOps(this.classInit, this.taskController).createDirectory(finalPath, {
                abortController: options.abortController,
                handleIgnoredError: onCreateDirError,
                ignoreErrors: { 409: true }
            });
        }
        else {
            this.taskController.setCurrentTask(LogLevel_1.LogLevel.debug, APITasks_1.APITasks.uploadFile, [sourcePath]);
        }
        return new Promise(async (resolve, reject) => {
            let headersPromise;
            if (entry && file) {
                headersPromise = self.getHeadersFromEntry(entry, mimeType);
            }
            else {
                headersPromise = self.getHeadersFromFile(sourcePath, mimeType);
            }
            APIAbortController_1.APIAbortController.THROW_IF_ABORTED(options.abortController);
            headersPromise.then(headers => {
                APIAbortController_1.APIAbortController.THROW_IF_ABORTED(options.abortController);
                let existPromise;
                if (!options.overwriteExisting || options.fnPrompt) {
                    const q = new url_search_params_1.default();
                    q.set("Filename", fileName);
                    q.set("Dir", targetDir);
                    q.set("Lastmodified", headers['Last-Modified']);
                    q.set("Filesize", headers['Content-Length']);
                    existPromise = this.com.postResponse(self.client.getServicePath("postupload"), q, undefined, undefined, options);
                }
                else {
                    existPromise = new Promise((resolveExists) => {
                        return resolveExists({ data: { statuscode: 102 } });
                    });
                }
                existPromise
                    .then(async (response) => {
                    APIAbortController_1.APIAbortController.THROW_IF_ABORTED(options.abortController);
                    const data = (response.data) ? response.data : null;
                    if (!data || (data.statuscode !== 202 && data.statuscode !== 404)) {
                        if (data && data.statuscode) {
                            if (data.statuscode === 102) {
                                let reply = "";
                                if (options.fnPrompt) {
                                    const res = await self.com.getOverwriteReply(options, finalPath, this.taskController);
                                    reply = res.reply;
                                }
                                if ((!reply && !options.overwriteExisting) || reply === "skip") {
                                    self.callProgress(LogLevel_1.LogLevel.trace, options, APITasks_1.APITasks.skipUpload, [finalPath]);
                                    return resolve(false);
                                }
                                else if (reply === "cancel") {
                                    reject(self.com.err.get(APIErrors_1.APIErrors.userAborted));
                                }
                            }
                            else {
                                return reject(self.com.err.get(APIErrors_1.APIErrors.upload, [sourcePath], APIErrors_1.APIErrors.httpErrorShort, [APIHTTPErrorCodes_1.APIHTTPErrorCodes.GET_CODE(data.statuscode)]));
                            }
                        }
                        else {
                            if (response.status > 399) {
                                return reject(self.com.err.get(APIErrors_1.APIErrors.upload, [sourcePath], APIErrors_1.APIErrors.httpErrorShort, [APIHTTPErrorCodes_1.APIHTTPErrorCodes.GET_CODE(response.status)]));
                            }
                            return reject(self.com.err.get(APIErrors_1.APIErrors.invalidServerReply));
                        }
                    }
                    let stream;
                    if (entry && file) {
                        stream = file;
                    }
                    else {
                        stream = fs_1.default.createReadStream(sourcePath);
                        stream.on("error", err => {
                            return reject(self.com.err.get(APIErrors_1.APIErrors.upload, [sourcePath], APIErrors_1.APIErrors.anyError, [err.message]));
                        });
                    }
                    this.uploadFile(sourcePath, finalPath, stream, headers, options)
                        .then(() => {
                        return resolve(true);
                    })
                        .catch(err => {
                        return rejectWithError(reject, err);
                    });
                })
                    .catch(err => {
                    return rejectWithError(reject, err);
                });
            })
                .catch(err => {
                return rejectWithError(reject, err);
            });
        });
    }
    callProgress(level, theOptions, apiTaskDef, content, pos = 0, length = 0) {
        if (theOptions._taskProgress) {
            theOptions._taskProgress.bytesTotal = length;
            theOptions._taskProgress.bytesDone = pos;
        }
        this.taskController.onStepProgress(level, theOptions, apiTaskDef, content, pos, length);
    }
    getHeadersFromFile(sourcePath, theMimeType) {
        return new Promise((resolve, reject) => {
            fs_1.default.stat(sourcePath, (error, stat) => {
                if (error) {
                    return reject(this.com.err.get(APIErrors_1.APIErrors.anyError, [error.message]));
                }
                else {
                    resolve({
                        "Accept": null,
                        "Content-Length": stat.size,
                        "Content-Type": theMimeType,
                        "Last-Modified": Math.floor(stat.mtimeMs),
                    });
                }
            });
        });
    }
    getHeadersFromEntry(theEntry, mime) {
        return new Promise((resolve) => {
            resolve({
                "Accept": null,
                "Content-Length": theEntry.size,
                "Content-Type": theEntry.mime || mime,
                "Last-Modified": theEntry.lastModified,
            });
        });
    }
    uploadFile(sourcePath, path, stream, headers, options) {
        const self = this;
        const url = self.client.getServicePath("image") + "/" + index_1.FSIServerClient.ENCODE_PATH(path);
        const bSafeHeaders = !modeNode;
        const bytesTotal = parseInt(headers["Content-Length"], 10);
        if (bSafeHeaders) {
            delete headers["Content-Length"];
        }
        const setProgress = (done, total) => {
            if (options._taskProgress) {
                options._taskProgress.bytesTotal = done;
                options._taskProgress.bytesDone = total;
            }
        };
        const onProgress = (done, total) => {
            self.callProgress(-1, options, APITasks_1.APITasks.uploadFile, [sourcePath], done, total);
        };
        const config = this.com.getAxiosRequestConfig(options, headers);
        config.onUploadProgress = (progressEvent) => {
            onProgress(progressEvent.loaded, progressEvent.total || 0);
        };
        setProgress(0, bytesTotal);
        return new Promise((resolve, reject) => {
            this.com.iAxios.put(url, stream, config)
                .then((res) => {
                setProgress(bytesTotal, bytesTotal);
                if (res.status > 399) {
                    reject(self.com.err.get(APIErrors_1.APIErrors.httpErrorShort, [APIHTTPErrorCodes_1.APIHTTPErrorCodes.GET_CODE(res.status)]));
                }
                else {
                    resolve(true);
                }
            })
                .catch(err => {
                reject(err);
            })
                .finally(() => {
                if (options.abortController)
                    options.abortController.release();
            });
        });
    }
}
exports.Upload = Upload;
